import { ethers } from 'ethers';
// 假设您的 ABI 在本地，路径需要根据您的项目调整
import AroundMarketABI from '../abis/AroundMarket.json';

/**
 * 预测市场合约 IAroundMarket 的 ethers.js 封装
 */
export class AroundMarketContract {
    /**
     * @type {ethers.Contract}
     */
    contract;

    /**
     * * @param {string} contractAddress 合约地址
     * @param {ethers.Provider | ethers.Signer} providerOrSigner ethers 提拱者或签名者
     */
    constructor(contractAddress, providerOrSigner) {
        this.contract = new ethers.Contract(
            contractAddress,
            AroundMarketABI.abi, // 替换为实际的 ABI JSON
            providerOrSigner
        );
    }

    // ------------------------------------
    // ⬇️ 读接口 (View Functions) 
    // ------------------------------------

    /**
     * 获取预言机 (Oracle) 地址。
     * @returns {Promise<string>} 预言机地址
     */
    async getOracleAddress() {
        try {
            return await this.contract.oracle();
        } catch (error) {
            console.error("Error fetching oracle address:", error);
            throw error;
        }
    }

    /**
     * 通过彩票 ID 和号码获取用户地址。
     * @param {string} marketId 市场 ID (uint256)
     * @param {number} ticketNumber 彩票号码 (uint64)
     * @returns {Promise<string>} 用户地址
     */
    async getRaffleTicketUser(marketId, ticketNumber) {
        try {
            return await this.contract.raffleTicketToUser(marketId, ticketNumber);
        } catch (error) {
            console.error("Error fetching raffle ticket user:", error);
            throw error;
        }
    }

    /**
     * 获取指定市场的费用信息。
     * @param {string} marketId 市场 ID (uint256)
     * @returns {Promise<object>} FeeInfo 结构体
     */
    async getMarketFeeInfo(marketId) {
        try {
            // ethers.js 会自动将 solidity struct 映射到 JS object
            return await this.contract.getFeeInfo(marketId);
        } catch (error) {
            console.error(`Error fetching fee info for market ${marketId}:`, error);
            throw error;
        }
    }

    /**
     * 获取用户在指定市场的头寸信息。
     * @param {string} user 用户地址
     * @param {string} marketId 市场 ID (uint256)
     * @returns {Promise<object>} UserPosition 结构体
     */
    async getUserMarketPosition(user, marketId) {
        try {
            return await this.contract.getUserPosition(user, marketId);
        } catch (error) {
            console.error(`Error fetching user position for market ${marketId}:`, error);
            throw error;
        }
    }

    /**
     * 获取指定市场的基本信息。
     * @param {string} marketId 市场 ID (uint256)
     * @returns {Promise<object>} MarketInfo 结构体
     */
    async getMarketDetails(marketId) {
        try {
            return await this.contract.getMarketInfo(marketId);
        } catch (error) {
            console.error(`Error fetching market info for market ${marketId}:`, error);
            throw error;
        }
    }

    /**
     * 获取指定市场的流动性信息。
     * @param {string} marketId 市场 ID (uint256)
     * @returns {Promise<object>} LiqudityInfo 结构体
     */
    async getMarketLiquidityInfo(marketId) {
        try {
            return await this.contract.getLiqudityInfo(marketId);
        } catch (error) {
            console.error(`Error fetching liquidity info for market ${marketId}:`, error);
            throw error;
        }
    }

    /**
     * 获取用户参与的市场 ID 列表长度
     * @param {string} user 用户地址
     * @returns {Promise<object>} length (uint256)
     */
    async getUserParticipateIdsLen(user) {
        try {
            return await this.contract.getUserParticipateIdsLen(user);
        } catch (error) {
            console.error("Error fetching user participate length:", error);
            throw error;
        }
    }

    /**
     * 根据索引获取用户参与的 marketId
     * @param {string} user 用户地址
     * @param {string|number} index 索引
     * @returns {Promise<object>} marketId (uint256)
     */
    async indexUserParticipateId(user, index) {
        try {
            return await this.contract.indexUserParticipateId(user, index);
        } catch (error) {
            console.error("Error fetching user participate id:", error);
            throw error;
        }
    }

    // ------------------------------------
    // ⬇️ 写接口 (Transaction Functions) 
    // ------------------------------------

    /**
     * 创建一个新的预测市场。
     * 注意：这个接口需要合约实例是用 Signer 初始化的。
     * @param {object} params CreateMarketParams 结构体
     * @param {number} params.period 市场持续时间 (uint32)
     * @param {string} params.expectVirtualLiquidity 期望的虚拟流动性 (uint128)
     * @param {string} params.thisMarketId 市场 ID (uint256)
     * @returns {Promise<ethers.TransactionResponse>} 交易响应
     */
    async createNewMarket(params) {
        try {
            // 将 JS Object 转换为 Solidity Struct 的参数格式
            return await this.contract.createMarket(params);
        } catch (error) {
            console.error("Error creating new market:", error);
            throw error;
        }
    }

    /**
     * 在市场中购买 Yes 或 No 份额。
     * @param bet 预测结果 (Yes: 1, No: 2)
     * @param {string} amount 投入的抵押品金额 (uint128)，需要使用 Wei 字符串或 BigNumber
     * @param {string} marketId 市场 ID (uint256)
     * @returns {Promise<ethers.TransactionResponse>} 交易响应
     */
    async buyShares(bet, amount, marketId) {
        try {
            return await this.contract.buy(bet, amount, marketId);
        } catch (error) {
            try {
                const parsed = this.contract.interface.parseError(error?.data || error);
                throw new Error(parsed?.name || error?.reason || error?.message || 'Buy failed');
            } catch (_) {
                throw error;
            }
        }
    }

    /**
     * 在市场中出售 Yes 或 No 份额。
     * @param bet 预测结果 (Yes: 1, No: 2)
     * @param {string} amount 出售的份额数量 (uint256)，需要使用 Wei 字符串或 BigNumber
     * @param {string} marketId 市场 ID (uint256)
     * @returns {Promise<ethers.TransactionResponse>} 交易响应
     */
    async sellShares(bet, amount, marketId) {
        try {
            return await this.contract.sell(bet, amount, marketId);
        } catch (error) {
            try {
                const parsed = this.contract.interface.parseError(error?.data || error);
                throw new Error(parsed?.name || error?.reason || error?.message || 'Sell failed');
            } catch (_) {
                throw error;
            }
        }
    }

    /**
     * 向市场添加流动性。
     * @param {string} marketId 市场 ID (uint256)
     * @param {string} amount 投入的抵押品金额 (uint128)，需要使用 Wei 字符串或 BigNumber
     * @returns {Promise<ethers.TransactionResponse>} 交易响应
     */
    async addMarketLiquidity(marketId, amount) {
        try {
            return await this.contract.addLiquidity(marketId, amount);
        } catch (error) {
            try {
                const parsed = this.contract.interface.parseError(error?.data || error);
                throw new Error(parsed?.name || error?.reason || error?.message || 'Add liquidity failed');
            } catch (_) {
                throw error;
            }
        }
    }

    /**
     * 从市场移除流动性。
     * @param {string} marketId 市场 ID (uint256)
     * @param {string} lpAmount 移除的 LP 份额数量 (uint128)，需要使用 Wei 字符串或 BigNumber
     * @returns {Promise<ethers.TransactionResponse>} 交易响应
     */
    async removeMarketLiquidity(marketId, lpAmount) {
        try {
            return await this.contract.removeLiquidity(marketId, lpAmount);
        } catch (error) {
            try {
                const parsed = this.contract.interface.parseError(error?.data || error);
                throw new Error(parsed?.name || error?.reason || error?.message || 'Remove liquidity failed');
            } catch (_) {
                throw error;
            }
        }
    }

    /**
     * 结算市场，领取收益。
     * @param {string} marketId 市场 ID (uint256)
     * @returns {Promise<ethers.TransactionResponse>} 交易响应
     */
    async redeemWinnings(marketId) {
        try {
            // 注意：Solidity 函数返回 uint256 winnings，但此处返回的是交易对象
            return await this.contract.redeemWinnings(marketId);
        } catch (error) {
            try {
                const parsed = this.contract.interface.parseError(error?.data || error);
                throw new Error(parsed?.name || error?.reason || error?.message || 'Redeem failed');
            } catch (_) {
                throw error;
            }
        }
    }

    async touchAllot(marketId) {
        try {
            return await this.contract.touchAllot(marketId);
        } catch (error) {
            try {
                const parsed = this.contract.interface.parseError(error?.data || error);
                throw new Error(parsed?.name || error?.reason || error?.message || 'Touch allot failed');
            } catch (_) {
                throw error;
            }
        }
    }
}
